import React, { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
    const [appContext, setAppContextState] = useState(() => {
        const host = window.location.hostname;
        const search = window.location.search;
        const params = new URLSearchParams(search);
        let source = params.get('source');

        if (!source) {
            source = sessionStorage.getItem('auth_source');
        }

        if (source === 'wrenchspot') {
            localStorage.setItem('hs_app_context', 'WRENCH');
            return 'WRENCH';
        }
        if (source === 'barkspot') {
            localStorage.setItem('hs_app_context', 'BARK');
            return 'BARK';
        }
        if (source === 'hangoutspot') {
            localStorage.setItem('hs_app_context', 'HANGOUT');
            return 'HANGOUT';
        }
        if (source === 'developer') {
            localStorage.setItem('hs_app_context', 'DEVELOPER');
            return 'DEVELOPER';
        }

        // Respect explicitly chosen workspace next
        const stored = localStorage.getItem('hs_app_context');
        if (stored) {
            return stored;
        }

        // Default to brand-specific workspace domains
        let initial = 'HANGOUT';
        if (host.includes('wrenchspot.local') || host.includes('wrenchspot.app')) {
            initial = 'WRENCH';
        } else if (host.includes('barkspot.local') || host.includes('barkspot.app')) {
            initial = 'BARK';
        } else if (host.includes('thehangoutspot.com') || host.includes('hangoutspot.local')) {
            initial = 'HANGOUT';
        }

        // Check if we are starting on the developer path
        if (window.location.pathname.startsWith('/developer')) {
            initial = 'DEVELOPER';
        }

        localStorage.setItem('hs_app_context', initial);
        return initial;
    });
    const [vehicles, setVehicles] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [pets, setPets] = useState([]);
    const [loadingPets, setLoadingPets] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const setAppContext = (context) => {
        localStorage.setItem('hs_app_context', context);
        setAppContextState(context);
    };

    // Entry Context Passing: Detect ?source= parameter on initial load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let source = params.get('source');
        let fromStorage = false;

        if (!source) {
            source = sessionStorage.getItem('auth_source');
            fromStorage = !!source;
        }

        if (source === 'wrenchspot') {
            setAppContext('WRENCH');
            if (fromStorage) {
                sessionStorage.removeItem('auth_source');
            } else {
                const url = new URL(window.location);
                url.searchParams.delete('source');
                window.history.replaceState({}, '', url);
            }
        } else if (source === 'barkspot') {
            setAppContext('BARK');
            if (fromStorage) {
                sessionStorage.removeItem('auth_source');
            } else {
                const url = new URL(window.location);
                url.searchParams.delete('source');
                window.history.replaceState({}, '', url);
            }
        } else if (source === 'hangoutspot') {
            setAppContext('HANGOUT');
            if (fromStorage) {
                sessionStorage.removeItem('auth_source');
            } else {
                const url = new URL(window.location);
                url.searchParams.delete('source');
                window.history.replaceState({}, '', url);
            }
        } else if (source === 'developer') {
            setAppContext('DEVELOPER');
            sessionStorage.removeItem('auth_source');
            document.cookie = "auth_source=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";

            const url = new URL(window.location);
            if (!fromStorage) {
                url.searchParams.delete('source');
            }
            url.pathname = '/developer';
            window.location.href = url.toString();
        }
    }, []);

    // Fetch active vehicles on WRENCH mount/switch
    const refreshVehicles = async () => {
        if (!isAuthenticated) return;
        setLoadingVehicles(true);
        try {
            const res = await fetch('/api/garage/vehicles');
            if (res.ok) {
                const data = await res.json();
                // Keep only active fleet
                setVehicles(data.filter(v => !v.isArchived));
            }
        } catch (err) {
            console.error('[AppContext] Failed to load vehicles:', err);
        } finally {
            setLoadingVehicles(false);
        }
    };

    // Fetch active pets on BARK mount/switch
    const refreshPets = async () => {
        if (!isAuthenticated) return;
        setLoadingPets(true);
        try {
            const res = await fetch('/api/bark/pets');
            if (res.ok) {
                const data = await res.json();
                setPets(data);
            }
        } catch (err) {
            console.error('[AppContext] Failed to load pets:', err);
        } finally {
            setLoadingPets(false);
        }
    };

    useEffect(() => {
        if (appContext === 'WRENCH' && isAuthenticated) {
            refreshVehicles();
        } else if (appContext === 'BARK' && isAuthenticated) {
            refreshPets();
        }
    }, [appContext, isAuthenticated]);

    // Keep class or body class updated with theming
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('theme-wrench', 'theme-hangout', 'theme-bark');
        if (appContext === 'WRENCH') {
            root.classList.add('theme-wrench');
        } else if (appContext === 'BARK') {
            root.classList.add('theme-bark');
        } else {
            root.classList.add('theme-hangout');
        }
    }, [appContext]);

    // Dynamically update PWA manifest link on context mount/change
    useEffect(() => {
        const link = document.querySelector('link[rel="manifest"]');
        if (link) {
            link.setAttribute('href', `/api/manifest.json?context=${appContext}`);
            console.log(`[PWA] Updated manifest href to: /api/manifest.json?context=${appContext}`);
        }

        let webAppTitleEl = document.querySelector('meta[name="apple-mobile-web-app-title"]');
        if (!webAppTitleEl) {
            webAppTitleEl = document.createElement('meta');
            webAppTitleEl.name = "apple-mobile-web-app-title";
            document.head.appendChild(webAppTitleEl);
        }
        
        let appTitle = 'Hangout Spot';
        if (appContext === 'WRENCH') appTitle = 'Wrench Spot';
        else if (appContext === 'BARK') appTitle = 'Bark Spot';
        webAppTitleEl.setAttribute('content', appTitle);

        let appleTouchIconEl = document.querySelector('link[rel="apple-touch-icon"]');
        if (appleTouchIconEl) {
            let iconUrl = '/apple-touch-icon.png?v=2';
            if (appContext === 'WRENCH') iconUrl = '/wrench-icon-192.png';
            else if (appContext === 'BARK') iconUrl = '/barkspot-icon-192.png';
            appleTouchIconEl.setAttribute('href', iconUrl);
        }

        let iconEl = document.querySelector('link[rel="icon"]');
        if (iconEl) {
            let logoUrl = '/logo.png';
            if (appContext === 'WRENCH') logoUrl = '/wrench-logo.webp';
            else if (appContext === 'BARK') logoUrl = '/barkspot-logo.webp';
            iconEl.setAttribute('href', logoUrl);
        }
    }, [appContext]);

    const updatePetInState = (updatedPet) => {
        if (!updatedPet || !updatedPet.id) return;
        setPets(prev => prev.map(p => p.id === updatedPet.id ? { ...p, ...updatedPet } : p));
    };

    return (
        <AppContext.Provider value={{ appContext, setAppContext, vehicles, refreshVehicles, loadingVehicles, pets, setPets, updatePetInState, refreshPets, loadingPets, isAuthenticated, setIsAuthenticated }}>
            {children}
        </AppContext.Provider>
    );
};
